import {Box, Group, Kbd, Modal, Table, Text} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {assertNever} from 'util/Types';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

export interface ShortcutsModalProps {
  opened: boolean;
  onClose: () => void;
}

interface ShortcutRowProps {
  description: string;
  tokens: Array<string | {key: string} | {icon: BenchlingIconName}>;
}

function ShortcutRow({description, tokens}: ShortcutRowProps): JSX.Element {
  return (
    <tr>
      <td>
        <Text fz="lg" weight={600}>
          {description}
        </Text>
      </td>
      <td>
        <Group spacing="xs" noWrap={true}>
          {tokens.map((token, index) =>
            typeof token === 'string' ? (
              <Text key={index} fz="lg" ml={index ? 'xs' : undefined} mr="xs" mb="md" mt="md">
                {token}
              </Text>
            ) : 'key' in token ? (
              <Kbd key={index} size="m" fz="lg" fw="normal" py="sm" px="md">
                {token.key}
              </Kbd>
            ) : 'icon' in token ? (
              <Box key={index} component="span" fz="xl">
                <BenchlingIcon icon={token.icon} />
              </Box>
            ) : (
              assertNever(token)
            )
          )}
        </Group>
      </td>
    </tr>
  );
}

/**
 * Modal that lists the shortcuts available in the analysis flowchart.
 */
export default function ShortcutsModal({opened, onClose}: ShortcutsModalProps): JSX.Element {
  const {getActionInfo} = useAnalysisFlowchartContext();
  const canCopy = getActionInfo(AnalysisFlowchartAction.COPY_TO_CLIPBOARD).enabled;
  const canPaste = getActionInfo(AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD).enabled;
  const modifierKeyText = keyboard.getOsSpecificShortcutText('Ctrl');

  return (
    <Modal opened={opened} onClose={onClose} title="Shortcuts" size="md">
      <Table verticalSpacing="sm" pb="sm">
        <tbody>
          {canCopy && <ShortcutRow description="Copy" tokens={[{key: modifierKeyText}, '+', {key: 'C'}]} />}
          {canPaste && <ShortcutRow description="Paste" tokens={[{key: modifierKeyText}, '+', {key: 'V'}]} />}
          <ShortcutRow description="Delete" tokens={[{key: 'Backspace'}]} />
          <ShortcutRow description="Select multiple steps" tokens={[{key: modifierKeyText}, '+', 'Click']} />
          <ShortcutRow
            description="Highlight steps"
            tokens={['Hold', {key: 'Shift'}, '+', 'Click and drag']}
          />
          <ShortcutRow
            description="Add step in a new branch"
            tokens={['Click', {icon: 'circle-arrow-right'}, 'on a step and drag out']}
          />
          <ShortcutRow
            description="Connect steps"
            tokens={['Click', {icon: 'circle-arrow-right'}, 'on a step and drag to another step']}
          />
        </tbody>
      </Table>
    </Modal>
  );
}
